import {
  Modal,
  ModalContent,
  ModalFooter
} from "@dealer-center/core-components";
import { useEffect, useState } from "react";
import { startTransition } from "react";
import { SaveAndCancel } from "@dealer-center/core-custom-components";
import type { resources } from "@dealer-center/ddc-pages-resources-interface";
import styled from "styled-components";
import {
  useResourceDelete,
  useResourceUpdate
} from "@dealer-center/ddc-pages-resources-fetch";
import { useGetAccountId } from "@dealer-center/ddc-api-account-fetch";

export interface SaveModalInterface {
  currentResources: resources[];
  existingResources: resources[];
  resetClicked: () => void;
}
interface hasChangesDetailInterface {
  newlyGrantedResources: resources[];
  newlyRevokedResources: resources[];
}
const StyledResourcesUnorderedList = styled.ul`
  padding-left: 1em;
  li {
    word-wrap: break-word;
    list-style-type: disc;
  }
`;
export const SaveModal = ({
  currentResources,
  existingResources,
  resetClicked
}: SaveModalInterface) => {
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [hasChangesDetail, setHasChangesDetail] =
    useState<hasChangesDetailInterface>({
      newlyGrantedResources: [],
      newlyRevokedResources: []
    });
  const updateMutation = useResourceUpdate();
  const deleteMutation = useResourceDelete();

  const selectedDealer = useGetAccountId();
  // alert("selectedAccount: " + selectedDealer);

  useEffect(() => {
    startTransition(() => {
      function compareBoolean(obj1: resources, obj2: resources) {
        return obj1.granted !== obj2.granted;
      }
      const anyChanges = () =>
        currentResources.some((element, index) =>
          compareBoolean(element, existingResources[index])
        );
      setHasChanges(anyChanges());
    });
  }, [currentResources, existingResources]);
  useEffect(() => {
    if (!showSaveModal) return;

    const getChanges = () => {
      const newlyGrantedResources = currentResources.filter(
        resource =>
          resource.granted &&
          !existingResources.find(
            res => res.name === resource.name && res.granted
          )
      );

      const newlyRevokedResources = existingResources.filter(
        resource =>
          resource.granted &&
          !currentResources.find(
            res => res.name === resource.name && res.granted
          )
      );
      return { newlyGrantedResources, newlyRevokedResources };
    };
    const { newlyGrantedResources, newlyRevokedResources } = getChanges();
    setHasChangesDetail({ newlyGrantedResources, newlyRevokedResources });
  }, [currentResources, existingResources, showSaveModal]);

  useEffect(() => {
    if (updateMutation.isSuccess) {
      setShowSaveModal(false);
    }
  }, [updateMutation.isSuccess]);

  useEffect(() => {
    if (deleteMutation.isSuccess) {
      setShowSaveModal(false);
    }
  }, [deleteMutation.isSuccess]);

  return (
    <>
      <SaveAndCancel
        analyticsEventName="Website Inventory Config Save Resources"
        cancelButtonText="Reset"
        isDisabled={{
          save: !hasChanges
        }}
        onCancel={resetClicked}
        onSave={() => setShowSaveModal(true)}
      />
      <Modal
        footer={{
          primary: {
            label: "unused"
          },
          footerComponent: (
            <ModalFooter>
              <SaveAndCancel
                analyticsEventName="Website Inventory Config Save Resources Modal"
                isSaving={updateMutation.isPending || deleteMutation.isPending}
                onCancel={() => setShowSaveModal(false)}
                onSave={() => {
                  if (hasChangesDetail.newlyGrantedResources.length > 0) {
                    updateMutation.mutate({
                      accountId: selectedDealer!,
                      resources: hasChangesDetail.newlyGrantedResources
                    });
                  }
                  if (hasChangesDetail.newlyRevokedResources.length > 0) {
                    deleteMutation.mutate({
                      accountId: selectedDealer!,
                      resources: hasChangesDetail.newlyRevokedResources
                    });
                  }
                }}
              />
            </ModalFooter>
          )
        }}
        header="Save Resources"
        show={showSaveModal}
        size="small"
        onHide={() => setShowSaveModal(false)}
      >
        <ModalContent>
          {hasChangesDetail.newlyGrantedResources.length > 0 && (
            <>
              <h3>Newly Granted</h3>
              <StyledResourcesUnorderedList>
                {hasChangesDetail.newlyGrantedResources.map(resource => (
                  <li key={resource.name}>{resource.name}</li>
                ))}
              </StyledResourcesUnorderedList>
            </>
          )}
          {hasChangesDetail.newlyRevokedResources.length > 0 && (
            <>
              <h3>Newly Revoked</h3>
              <StyledResourcesUnorderedList>
                {hasChangesDetail.newlyRevokedResources.map(resource => (
                  <li key={resource.name}>{resource.name}</li>
                ))}
              </StyledResourcesUnorderedList>
            </>
          )}
        </ModalContent>
      </Modal>
    </>
  );
};
